import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as PendingIcon,
  Inventory as InventoryIcon,
  PlaylistAddCheck as ApproveIcon,
  DoneAll as CompleteIcon,
  Undo as UndoIcon,
  Autorenew as InProgressIcon,
  Refresh as RefreshIcon,
  CloudUpload as CloudUploadIcon,
  PictureAsPdf as PdfIcon,
  PointOfSale as SoldIcon,
  SwapHoriz as SwapHorizIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { inventoryAPI } from '../../services/api';
import Breadcrumbs from '../../components/Breadcrumbs';
import { useOrg } from '../../contexts/OrgContext';

const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' 
  ? 'https://barebiologics-api-bdgjaefwc4h7d9gu.eastus2-01.azurewebsites.net/api'
  : 'http://localhost:8080/api');

const MAX_SKUS = 1000;

const WORKFLOW_STATES = [
  {
    value: 'PENDING',
    label: 'Pending Approval',
    description: 'New tissue IDs awaiting initial verification before edits can be made.',
    color: 'warning',
    icon: <PendingIcon fontSize="small" />
  },
  {
    value: 'IN_PROGRESS',
    label: 'In Progress (Quarantine)',
    description: 'Approved tissue IDs actively being processed within quarantine.',
    color: 'info',
    icon: <InProgressIcon fontSize="small" />
  },
  {
    value: 'AVAILABLE',
    label: 'Completed / Released',
    description: 'Tissue IDs cleared for inventory management and visible downstream.',
    color: 'success',
    icon: <CheckCircleIcon fontSize="small" />
  },
  {
    value: 'ALLOCATED',
    label: 'Sold / Allocated',
    description: 'Tissue IDs that have been allocated or sold and can no longer be moved.',
    color: 'secondary',
    icon: <SoldIcon fontSize="small" />
  }
];

const normalizeSkuStatus = (status) => {
  if (!status) return 'PENDING';
  const normalized = status.toUpperCase();
  if (['AVAILABLE', 'COMPLETED', 'PASSED', 'APPROVED', 'RELEASED'].includes(normalized)) {
    return 'AVAILABLE';
  }
  if (['ALLOCATED', 'SOLD', 'DELIVERED', 'FULFILLED', 'RESERVED'].includes(normalized)) {
    return 'ALLOCATED';
  }
  if (normalized === 'IN_PROGRESS' || normalized === 'RECEIVING' || normalized === 'PROCESSING' || normalized === 'QUARANTINE') {
    return 'IN_PROGRESS';
  }
  return 'PENDING';
};

const normalizeBatchStatus = (status) => {
  if (!status) return 'PENDING';
  const normalized = status.toUpperCase();
  if (normalized === 'PASSED' || normalized === 'COMPLETED') return 'COMPLETED';
  if (normalized === 'IN_PROGRESS' || normalized === 'RECEIVING' || normalized === 'PROCESSING') return 'IN_PROGRESS';
  if (normalized === 'PENDING' || normalized === 'NEW') return 'PENDING';
  return normalized;
};

// QC Status Options for GDP compliance
const QC_STATUS_OPTIONS = [
  { value: 'QUARANTINE_NOT_LABELED', label: 'Quarantine/Not Labeled', color: '#ef4444', bgColor: '#fef2f2' },
  { value: 'APPROVED_NOT_LABELED', label: 'Approved/Not Labeled', color: '#f97316', bgColor: '#fff7ed' },
  { value: 'APPROVED_LABELED', label: 'Approved/Labeled', color: '#3b82f6', bgColor: '#eff6ff' },
  { value: 'APPROVED_FOR_DISTRIBUTION', label: 'Approved for Distribution', color: '#22c55e', bgColor: '#f0fdf4' }
];

const formatDate = (date) => {
  if (!date) return '-';
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return '-';
  return parsed.toLocaleString();
};

const ReceiveInventoryBatchDetail = ({ currentCompany }) => {
  const navigate = useNavigate();
  const { batchId } = useParams();
  const { org: contextOrg } = useOrg();

  const effectiveOrg = (currentCompany || contextOrg || '').toLowerCase();

  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [batch, setBatch] = useState(null);
  const [batchStatus, setBatchStatus] = useState('PENDING');
  const [skus, setSkus] = useState([]);
  const [activeState, setActiveState] = useState('PENDING');
  const [selectedSkuIds, setSelectedSkuIds] = useState([]);
  const [certificateUploading, setCertificateUploading] = useState(false);
  const fileInputRef = useRef(null);
  const hasInitializedState = useRef(false);
  
  // Inventory designation state
  const [designationDialogOpen, setDesignationDialogOpen] = useState(false);
  const [designationForm, setDesignationForm] = useState({
    qcStatus: '',
    location: '',
    reason: ''
  });
  const [designationLocations, setDesignationLocations] = useState([]);
  const [designationLoading, setDesignationLoading] = useState(false);
  
  // State for selecting designated SKUs (right side table)
  const [selectedDesignatedSkuIds, setSelectedDesignatedSkuIds] = useState([]);
  const [redesignateDialogOpen, setRedesignateDialogOpen] = useState(false);
  const [redesignateForm, setRedesignateForm] = useState({
    qcStatus: '',
    location: '',
    reason: ''
  });

  const resetSelection = useCallback(() => {
    setSelectedSkuIds([]);
  }, []);
  
  const resetDesignatedSelection = useCallback(() => {
    setSelectedDesignatedSkuIds([]);
  }, []);

  const loadBatch = useCallback(async () => {
    if (!batchId) return;
    setLoading(true);
    setError('');

    try {
      const batchResponse = await inventoryAPI.getBatchById(batchId);
      const batchData = batchResponse?.data;

      if (!batchData) {
        throw new Error('Batch not found');
      }

      setBatch(batchData);
      setBatchStatus(normalizeBatchStatus(batchData.qcStatus));

      const orgForSkus = batchData.organization || (currentCompany ? currentCompany.toUpperCase() : undefined);
      const skuResponse = await inventoryAPI.getSKUs(orgForSkus, MAX_SKUS, { inventoryId: batchId });
      const skuRows = (skuResponse?.data || []).map((sku) => {
        let normalizedStatus = normalizeSkuStatus(sku.status);
        const allocated = Boolean(sku.allocatedToOrderId);
        if (allocated && normalizedStatus !== 'ALLOCATED') {
          normalizedStatus = 'ALLOCATED';
        }
        return {
          id: sku.id,
          sku: sku.sku,
          tissueId: sku.tissueId,
          status: normalizedStatus,
          rawStatus: sku.status,
          costPerUnit: sku.costPerUnit,
          createdAt: sku.createdAt,
          lastUpdatedAt: sku.lastUpdatedAt,
          warehouse: sku.warehouse,
          zone: sku.zone,
          shelf: sku.shelf,
          bin: sku.bin,
          allocatedToOrderId: sku.allocatedToOrderId || null,
          qcStatus: sku.qcStatus || null, // Add QC status for designation tracking
        };
      });

      setSkus(skuRows);
      resetSelection();

      const derivedStatus = await syncBatchStatus(batchData, skuRows, { allowRemoteUpdate: true });
      if (!hasInitializedState.current) {
        if (skuRows.some((row) => row.status === 'PENDING')) {
          setActiveState('PENDING');
        } else if (skuRows.some((row) => row.status === 'IN_PROGRESS')) {
          setActiveState('IN_PROGRESS');
        } else if (skuRows.some((row) => row.status === 'AVAILABLE')) {
          setActiveState('AVAILABLE');
        } else if (skuRows.some((row) => row.status === 'ALLOCATED')) {
          setActiveState('ALLOCATED');
        } else if (derivedStatus === 'COMPLETED') {
          setActiveState('AVAILABLE');
        } else {
          setActiveState('PENDING');
        }
        hasInitializedState.current = true;
      }
    } catch (err) {
      console.error('❌ Failed to load batch detail:', err);
      setError(err?.message || 'Unable to load batch');
    } finally {
      setLoading(false);
    }
  }, [batchId, currentCompany, resetSelection]);

  useEffect(() => {
    loadBatch();
  }, [loadBatch]);

  // Fetch locations for designation
  const fetchDesignationLocations = async () => {
    try {
      const orgUpper = (currentCompany || contextOrg || '').toUpperCase();
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/inventory/locations?organization=${orgUpper}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        const data = await response.json();
        setDesignationLocations(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
      setDesignationLocations([]);
    }
  };

  // Open designation dialog
  const handleOpenDesignation = () => {
    if (selectedSkuIds.length === 0) {
      setError('Please select at least one tissue ID');
      return;
    }
    setDesignationForm({ qcStatus: '', location: '', reason: '' });
    fetchDesignationLocations();
    setDesignationDialogOpen(true);
  };

  // Handle bulk designation update
  const handleBulkDesignation = async () => {
    if (!designationForm.qcStatus) {
      setError('Please select a QC status');
      return;
    }

    if (!designationForm.reason.trim()) {
      setError('Please provide a reason for this change (GDP compliance)');
      return;
    }

    setDesignationLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/inventory/skus/bulk-edit`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          skuIds: selectedSkuIds,
          newQcStatus: designationForm.qcStatus,
          newLocation: designationForm.location || undefined,
          reason: designationForm.reason.trim()
        })
      });

      if (response.ok) {
        const result = await response.json();
        setSuccessMessage(`Successfully updated ${result.data.updated} tissue IDs with GDP audit trail`);
        setDesignationDialogOpen(false);
        setSelectedSkuIds([]);
        setDesignationForm({ qcStatus: '', location: '', reason: '' });
        await loadBatch(); // Refresh batch data
      } else {
        const error = await response.json();
        setError(error.message || 'Failed to update tissue IDs');
      }
    } catch (error) {
      console.error('Error updating SKUs:', error);
      setError('Failed to update tissue IDs');
    } finally {
      setDesignationLoading(false);
    }
  };

  const syncBatchStatus = useCallback(async (batchRecord, skuList, options = {}) => {
    const { allowRemoteUpdate = false } = options;
    if (!batchRecord) return;

    const total = skuList.length;
    let pendingCount = 0;
    let releasedCount = 0;

    skuList.forEach((sku) => {
      const normalized = normalizeSkuStatus(sku.status);
      if (normalized === 'PENDING') pendingCount += 1;
      if (normalized === 'AVAILABLE' || normalized === 'ALLOCATED') releasedCount += 1;
    });

    let derivedStatus = 'PENDING';
    if (total === 0) {
      derivedStatus = 'PENDING';
    } else if (releasedCount === total) {
      derivedStatus = 'COMPLETED';
    } else if (pendingCount === total) {
      derivedStatus = 'PENDING';
    } else {
      derivedStatus = 'IN_PROGRESS';
    }

    setBatchStatus(derivedStatus);

    if (allowRemoteUpdate && normalizeBatchStatus(batchRecord.qcStatus) !== derivedStatus) {
      try {
        await inventoryAPI.updateBatchStatus(batchRecord.id, { status: derivedStatus });
        setBatch((prev) => prev ? { ...prev, qcStatus: derivedStatus } : prev);
      } catch (err) {
        console.warn('⚠️ Unable to sync batch status remotely:', err);
      }
    }
    return derivedStatus;
  }, []);

  const workflowGroups = useMemo(() => {
    const groups = {
      PENDING: [],
      IN_PROGRESS: [],
      AVAILABLE: [],
      ALLOCATED: []
    };

    skus.forEach((sku) => {
      const group = normalizeSkuStatus(sku.status);
      groups[group] = [...(groups[group] || []), sku];
    });

    return groups;
  }, [skus]);

  // Separate AVAILABLE SKUs into undesignated and designated groups
  const availableSkusGroups = useMemo(() => {
    const available = workflowGroups.AVAILABLE || [];
    const undesignated = available.filter(sku => !sku.qcStatus || sku.qcStatus.trim() === '');
    
    const designatedByStatus = {
      QUARANTINE_NOT_LABELED: [],
      APPROVED_NOT_LABELED: [],
      APPROVED_LABELED: [],
      APPROVED_FOR_DISTRIBUTION: []
    };
    
    available.forEach(sku => {
      if (sku.qcStatus && sku.qcStatus.trim() !== '') {
        // Convert qcStatus to match designatedByStatus keys (e.g., 'Approved/Not Labeled' -> 'APPROVED_NOT_LABELED')
        const status = sku.qcStatus.toUpperCase().replace(/ /g, '_').replace(/\//g, '_');
        if (designatedByStatus[status]) {
          designatedByStatus[status].push(sku);
        }
      }
    });

    return { undesignated, designatedByStatus };
  }, [workflowGroups.AVAILABLE]);

  const currentRows = workflowGroups[activeState] || [];
  const isSelectionDisabled = activeState === 'ALLOCATED';
  const rowsSelectedState = useMemo(() => new Set(selectedSkuIds), [selectedSkuIds]);

  // For AVAILABLE tab, use undesignated SKUs for selection
  const selectableRows = activeState === 'AVAILABLE' 
    ? (availableSkusGroups?.undesignated || [])
    : currentRows;

  useEffect(() => {
    // Keep selected rows aligned with active state
    const currentIds = new Set(selectableRows.map((row) => row.id));
    setSelectedSkuIds((prev) => prev.filter((id) => currentIds.has(id)));
  }, [activeState, selectableRows]);

  const handleToggleAll = (event) => {
    if (isSelectionDisabled) return;
    if (event.target.checked) {
      const ids = selectableRows.map((row) => row.id);
      setSelectedSkuIds(ids);
    } else {
      resetSelection();
    }
  };

  const handleToggleRow = (skuId) => {
    setSelectedSkuIds((prev) => {
      if (prev.includes(skuId)) {
        return prev.filter((id) => id !== skuId);
      }
      return [...prev, skuId];
    });
  };
  
  // Toggle selection for designated SKUs
  const handleToggleDesignatedRow = (skuId) => {
    setSelectedDesignatedSkuIds((prev) => {
      if (prev.includes(skuId)) {
        return prev.filter((id) => id !== skuId);
      }
      return [...prev, skuId];
    });
  };
  
  // Select all designated SKUs
  const handleToggleAllDesignated = (event) => {
    if (event.target.checked) {
      const allDesignatedIds = Object.values(availableSkusGroups?.designatedByStatus || {})
        .flat()
        .map((sku) => sku.id);
      setSelectedDesignatedSkuIds(allDesignatedIds);
    } else {
      resetDesignatedSelection();
    }
  };
  
  // Open redesignate dialog for designated SKUs
  const handleOpenRedesignate = () => {
    if (selectedDesignatedSkuIds.length === 0) {
      setError('Please select at least one tissue ID');
      return;
    }
    setRedesignateForm({ qcStatus: '', location: '', reason: '' });
    fetchDesignationLocations();
    setRedesignateDialogOpen(true);
  };
  
  // Handle redesignation (change QC status of already designated SKUs)
  const handleRedesignate = async () => {
    if (!redesignateForm.reason.trim()) {
      setError('Please provide a reason for this change (GDP compliance)');
      return;
    }

    setDesignationLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/inventory/skus/bulk-edit`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          skuIds: selectedDesignatedSkuIds,
          newQcStatus: redesignateForm.qcStatus || null, // null to clear designation
          newLocation: redesignateForm.location || undefined,
          reason: redesignateForm.reason.trim()
        })
      });

      if (response.ok) {
        const result = await response.json();
        const action = redesignateForm.qcStatus ? 'redesignated' : 'returned to undesignated';
        setSuccessMessage(`Successfully ${action} ${result.data.updated} tissue IDs with GDP audit trail`);
        setRedesignateDialogOpen(false);
        setSelectedDesignatedSkuIds([]);
        setRedesignateForm({ qcStatus: '', location: '', reason: '' });
        await loadBatch(); // Refresh batch data
      } else {
        const error = await response.json();
        setError(error.message || 'Failed to update tissue IDs');
      }
    } catch (error) {
      console.error('Error updating SKUs:', error);
      setError('Failed to update tissue IDs');
    } finally {
      setDesignationLoading(false);
    }
  };

  const handleBulkStatusChange = async (targetStatus) => {
    if (!batch || selectedSkuIds.length === 0 || isSelectionDisabled) return;

    setUpdating(true);
    setError('');
    setSuccessMessage('');

    try {
      await inventoryAPI.bulkUpdateSkus({
        skuIds: selectedSkuIds,
        status: targetStatus
      });

      const nextSkus = skus.map((sku) => {
        if (!selectedSkuIds.includes(sku.id)) return sku;
        return {
          ...sku,
          status: normalizeSkuStatus(targetStatus),
          rawStatus: targetStatus
        };
      });

      setSkus(nextSkus);
      resetSelection();
      await syncBatchStatus(batch, nextSkus, { allowRemoteUpdate: true });
      setSuccessMessage('Status updated successfully.');
    } catch (err) {
      console.error('❌ Failed to update SKU status:', err);
      setError(err?.message || 'Unable to update selected tissue IDs');
    } finally {
      setUpdating(false);
    }
  };

  const handleRefresh = async () => {
    await loadBatch();
  };

const handleCertificateUpload = async (event) => {
  const file = event.target.files?.[0];
  if (!file || !batch) return;

  if (file.type !== 'application/pdf') {
    setError('Only PDF files are supported for QC uploads.');
    event.target.value = '';
    return;
  }

  setCertificateUploading(true);
  setError('');
  setSuccessMessage('');

  try {
    await inventoryAPI.uploadBatchCertificate(batch.id, file);
    setSuccessMessage('QC certificate uploaded successfully.');
    await loadBatch();
  } catch (err) {
    console.error('❌ Failed to upload certificate:', err);
    setError(err?.message || 'Unable to upload QC certificate.');
  } finally {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    } else {
      event.target.value = '';
    }
    setCertificateUploading(false);
  }
};

  const totalSkus = skus.length;
  const pendingCount = workflowGroups.PENDING?.length || 0;
  const inProgressCount = workflowGroups.IN_PROGRESS?.length || 0;
  const completedCount = workflowGroups.AVAILABLE?.length || 0;
  const allocatedCount = workflowGroups.ALLOCATED?.length || 0;

  const actionButtons = useMemo(() => {
    switch (activeState) {
      case 'PENDING':
        return [
          {
            label: 'Approve Selected',
            icon: <ApproveIcon />,
            color: 'primary',
            targetStatus: 'IN_PROGRESS'
          }
        ];
      case 'IN_PROGRESS':
        return [
          {
            label: 'Move Back to Pending',
            icon: <UndoIcon />,
            color: 'warning',
            targetStatus: 'PENDING'
          },
          {
            label: 'Mark as Completed',
            icon: <CompleteIcon />,
            color: 'success',
            targetStatus: 'AVAILABLE'
          }
        ];
      case 'AVAILABLE':
        return [
          {
            label: 'Return to Quarantine',
            icon: <UndoIcon />,
            color: 'warning',
            targetStatus: 'IN_PROGRESS'
          }
        ];
      case 'ALLOCATED':
        return [];
      default:
        return [];
    }
  }, [activeState]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!batch) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          Unable to locate this batch.
        </Alert>
        <Button variant="contained" onClick={() => navigate(-1)} startIcon={<ArrowBackIcon />}>
          Back
        </Button>
      </Box>
    );
  }

  const breadcrumbItems = [
    { label: 'Dashboard', path: `/internal/${effectiveOrg}/dashboard` },
    { label: 'Receive Inventory', path: `/internal/${effectiveOrg}/receive-inventory` },
    { label: batch.batchId || 'Batch Detail' }
  ];

  const statusMeta = WORKFLOW_STATES.find((state) => state.value === activeState);

  return (
    <Box sx={{ pb: 6 }}>
      <Breadcrumbs items={breadcrumbItems} />

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2, mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(`/internal/${effectiveOrg}/receive-inventory`)}
          >
            Back to Receive Inventory
          </Button>
          <Chip
            icon={batchStatus === 'COMPLETED' ? <CheckCircleIcon /> : batchStatus === 'IN_PROGRESS' ? <InProgressIcon /> : <PendingIcon />}
            color={batchStatus === 'COMPLETED' ? 'success' : batchStatus === 'IN_PROGRESS' ? 'info' : 'warning'}
            label={`Batch Status: ${batchStatus.replace('_', ' ')}`}
            sx={{ fontWeight: 600 }}
          />
        </Stack>
        <Button variant="text" startIcon={<RefreshIcon />} onClick={handleRefresh}>
          Refresh
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}

      <Card sx={{ mb: 4, borderRadius: 3, boxShadow: '0 12px 30px rgba(3,149,130,0.12)' }}>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Stack spacing={1.5}>
                <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                  Batch
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <InventoryIcon sx={{ color: '#027A6B' }} />
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {batch.batchId}
                  </Typography>
                </Stack>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {batch.product?.name}
                </Typography>
                <Typography variant="body2">
                  Lot: <strong>{batch.lotNumber}</strong>
                </Typography>
              </Stack>
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack spacing={1.5}>
                <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                  Timeline
                </Typography>
                <Typography variant="body2">
                  Received: <strong>{formatDate(batch.receivedAt || batch.createdAt)}</strong>
                </Typography>
                <Typography variant="body2">
                  Manufacturing: <strong>{formatDate(batch.manufacturingDate)}</strong>
                </Typography>
                <Typography variant="body2">
                  Expiration: <strong>{formatDate(batch.expirationDate)}</strong>
                </Typography>
              </Stack>
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack spacing={1.5}>
                <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                  Tissue IDs
                </Typography>
                <Stack direction="row" spacing={2}>
                  <Stack>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#f59e0b' }}>
                      {pendingCount}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Pending Approval
                    </Typography>
                  </Stack>
                  <Stack>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#3b82f6' }}>
                      {inProgressCount}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      In Progress
                    </Typography>
                  </Stack>
                  <Stack>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#10b981' }}>
                      {completedCount}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Completed
                    </Typography>
                  </Stack>
                  <Stack>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#6366F1' }}>
                      {allocatedCount}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Sold / Allocated
                    </Typography>
                  </Stack>
                </Stack>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Total tissue IDs: {totalSkus}
                </Typography>
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <Stack spacing={1.5}>
                <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                  Quality Certificate
                </Typography>
                {batch.qcDocumentUrl ? (
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <PdfIcon sx={{ color: '#e11d48' }} />
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {batch.qcDocumentName || 'QC Certificate'}
                      </Typography>
                    </Stack>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Uploaded {formatDate(batch.qcDocumentUploadedAt)}{' '}
                      {batch.qcDocumentUploadedByName || batch.qcDocumentUploadedBy
                        ? `by ${batch.qcDocumentUploadedByName || batch.qcDocumentUploadedBy}`
                        : ''}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      <Button
                        component="a"
                        href={batch.qcDocumentUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="outlined"
                        startIcon={<PdfIcon />}
                        sx={{ textTransform: 'none' }}
                      >
                        View Certificate
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={<CloudUploadIcon />}
                        onClick={() => fileInputRef.current?.click()}
                        disabled={certificateUploading}
                        sx={{ textTransform: 'none' }}
                      >
                        {certificateUploading ? 'Uploading...' : 'Replace PDF'}
                      </Button>
                    </Stack>
                  </Stack>
                ) : (
                  <Stack spacing={1.5}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Upload the QC certificate PDF to release this batch from quarantine.
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<CloudUploadIcon />}
                      onClick={() => fileInputRef.current?.click()}
                      disabled={certificateUploading}
                      sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
                    >
                      {certificateUploading ? 'Uploading...' : 'Upload QC PDF'}
                    </Button>
                  </Stack>
                )}
                <input
                  type="file"
                  accept="application/pdf"
                  hidden
                  ref={fileInputRef}
                  onChange={handleCertificateUpload}
                />
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ borderRadius: 3 }}>
        <CardContent>
          <Stack spacing={2}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Batch Workflow Control
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Use the tabs below to verify and move tissue IDs through quarantine. Items appear in Inventory Management only after they are marked as completed.
            </Typography>
            <Tabs
              value={activeState}
              onChange={(_, value) => {
                setActiveState(value);
                resetSelection();
              }}
              variant="scrollable"
              allowScrollButtonsMobile
            >
              {WORKFLOW_STATES.map((state) => (
                <Tab
                  key={state.value}
                  value={state.value}
                  label={
                    <Stack direction="row" spacing={1} alignItems="center">
                      {state.icon}
                      <Typography component="span" sx={{ fontWeight: 600 }}>
                        {state.label}
                      </Typography>
                      <Chip
                        size="small"
                        color={state.color}
                        label={workflowGroups[state.value]?.length || 0}
                        sx={{ fontWeight: 600 }}
                      />
                    </Stack>
                  }
                />
              ))}
            </Tabs>

            {statusMeta && (
              <Alert severity={statusMeta.color === 'success' ? 'success' : statusMeta.color === 'info' ? 'info' : 'warning'}>
                {statusMeta.description}
              </Alert>
            )}

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between">
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {isSelectionDisabled
                  ? 'Sold / allocated tissue IDs are locked for audit and cannot be moved.'
                  : activeState === 'AVAILABLE'
                    ? selectedSkuIds.length > 0
                      ? `${selectedSkuIds.length} undesignated tissue ID${selectedSkuIds.length > 1 ? 's' : ''} selected`
                      : `${availableSkusGroups?.undesignated?.length || 0} undesignated, ${Object.values(availableSkusGroups?.designatedByStatus || {}).reduce((sum, arr) => sum + arr.length, 0)} designated`
                    : selectedSkuIds.length > 0
                      ? `${selectedSkuIds.length} tissue ID${selectedSkuIds.length > 1 ? 's' : ''} selected`
                      : `Total tissue IDs in this step: ${currentRows.length}`}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {actionButtons.length > 0 && actionButtons.map((action) => (
                  <Button
                    key={action.label}
                    variant="contained"
                    color={action.color}
                    startIcon={action.icon}
                    disabled={selectedSkuIds.length === 0 || updating}
                    onClick={() => handleBulkStatusChange(action.targetStatus)}
                    sx={{ textTransform: 'none' }}
                  >
                    {action.label}
                  </Button>
                ))}
                {selectedSkuIds.length > 0 && activeState === 'AVAILABLE' && (
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={updating || isSelectionDisabled}
                    onClick={handleOpenDesignation}
                    sx={{ textTransform: 'none', bgcolor: '#039582', '&:hover': { bgcolor: '#027A6B' } }}
                  >
                    Designate Inventory
                  </Button>
                )}
              </Stack>
            </Stack>

            <Divider />

            {activeState === 'AVAILABLE' && availableSkusGroups ? (
              // Two-table layout for Completed/Released tab
              <Grid container spacing={3}>
                {/* Left Table: Undesignated SKUs */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ border: '2px solid #f59e0b' }}>
                    <CardContent>
                      <Stack spacing={2}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: '#f59e0b' }}>
                            Undesignated Inventory
                          </Typography>
                          <Chip 
                            label={availableSkusGroups.undesignated.length} 
                            size="small" 
                            color="warning"
                            sx={{ fontWeight: 600 }}
                          />
                        </Box>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          Select tissue IDs to designate with QC status and location
                        </Typography>
                        <TableContainer sx={{ maxHeight: 480, borderRadius: 2 }}>
                          <Table stickyHeader size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell padding="checkbox">
                                  <Checkbox
                                    color="primary"
                                    indeterminate={selectedSkuIds.length > 0 && selectedSkuIds.length < availableSkusGroups.undesignated.length}
                                    checked={availableSkusGroups.undesignated.length > 0 && selectedSkuIds.length === availableSkusGroups.undesignated.length}
                                    onChange={handleToggleAll}
                                    disabled={isSelectionDisabled || availableSkusGroups.undesignated.length === 0}
                                  />
                                </TableCell>
                                <TableCell>Tissue ID</TableCell>
                                <TableCell>Location</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {availableSkusGroups.undesignated.length === 0 ? (
                                <TableRow>
                                  <TableCell colSpan={3}>
                                    <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                                      <Typography variant="body2">All inventory has been designated.</Typography>
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              ) : (
                                availableSkusGroups.undesignated.map((row) => (
                                  <TableRow hover key={row.id} selected={rowsSelectedState.has(row.id)}>
                                    <TableCell padding="checkbox">
                                      <Checkbox
                                        color="primary"
                                        checked={rowsSelectedState.has(row.id)}
                                        onChange={() => handleToggleRow(row.id)}
                                        disabled={isSelectionDisabled}
                                      />
                                    </TableCell>
                                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                                      {row.tissueId}
                                    </TableCell>
                                    <TableCell>
                                      <Tooltip
                                        title={
                                          row.warehouse
                                            ? `Warehouse: ${row.warehouse} • Zone: ${row.zone || '-'} • Shelf: ${row.shelf || '-'} • Bin: ${row.bin || '-'}`
                                            : 'Location not assigned'
                                        }
                                      >
                                        <span>
                                          {[row.warehouse, row.zone, row.shelf, row.bin].filter(Boolean).join(' / ') || '—'}
                                        </span>
                                      </Tooltip>
                                    </TableCell>
                                  </TableRow>
                                ))
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Right Table: Designated SKUs by Status */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ border: '2px solid #10b981' }}>
                    <CardContent>
                      <Stack spacing={2}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="h6" sx={{ fontWeight: 700, color: '#10b981' }}>
                            Designated Inventory
                          </Typography>
                          <Chip 
                            label={
                              availableSkusGroups.designatedByStatus.QUARANTINE_NOT_LABELED.length +
                              availableSkusGroups.designatedByStatus.APPROVED_NOT_LABELED.length +
                              availableSkusGroups.designatedByStatus.APPROVED_LABELED.length +
                              availableSkusGroups.designatedByStatus.APPROVED_FOR_DISTRIBUTION.length
                            } 
                            size="small" 
                            color="success"
                            sx={{ fontWeight: 600 }}
                          />
                        </Box>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {selectedDesignatedSkuIds.length > 0 
                            ? `${selectedDesignatedSkuIds.length} selected - choose action below`
                            : 'Select items to change their designation'}
                        </Typography>
                        
                        {/* Action buttons for designated SKUs */}
                        {selectedDesignatedSkuIds.length > 0 && (
                          <Stack direction="row" spacing={1} flexWrap="wrap">
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={<SwapHorizIcon />}
                              onClick={handleOpenRedesignate}
                              sx={{ bgcolor: '#8b5cf6', '&:hover': { bgcolor: '#7c3aed' } }}
                            >
                              Change Designation
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<ClearIcon />}
                              onClick={() => {
                                setRedesignateForm({ qcStatus: '', location: '', reason: '' });
                                fetchDesignationLocations();
                                setRedesignateDialogOpen(true);
                              }}
                              sx={{ borderColor: '#f59e0b', color: '#f59e0b' }}
                            >
                              Clear Designation
                            </Button>
                          </Stack>
                        )}
                        
                        <TableContainer sx={{ maxHeight: 480, borderRadius: 2 }}>
                          <Table stickyHeader size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell padding="checkbox">
                                  <Checkbox
                                    color="primary"
                                    indeterminate={
                                      selectedDesignatedSkuIds.length > 0 && 
                                      selectedDesignatedSkuIds.length < Object.values(availableSkusGroups.designatedByStatus).flat().length
                                    }
                                    checked={
                                      Object.values(availableSkusGroups.designatedByStatus).flat().length > 0 &&
                                      selectedDesignatedSkuIds.length === Object.values(availableSkusGroups.designatedByStatus).flat().length
                                    }
                                    onChange={handleToggleAllDesignated}
                                    disabled={Object.values(availableSkusGroups.designatedByStatus).flat().length === 0}
                                  />
                                </TableCell>
                                <TableCell>QC Status</TableCell>
                                <TableCell>Tissue ID</TableCell>
                                <TableCell>Location</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {Object.entries(availableSkusGroups.designatedByStatus).map(([status, statusSkus]) => {
                                if (statusSkus.length === 0) return null;
                                const statusConfig = QC_STATUS_OPTIONS.find(opt => opt.value === status);
                                return statusSkus.map((row, idx) => (
                                  <TableRow 
                                    hover 
                                    key={row.id}
                                    selected={selectedDesignatedSkuIds.includes(row.id)}
                                    onClick={() => handleToggleDesignatedRow(row.id)}
                                    sx={{ cursor: 'pointer' }}
                                  >
                                    <TableCell padding="checkbox">
                                      <Checkbox
                                        color="primary"
                                        checked={selectedDesignatedSkuIds.includes(row.id)}
                                        onChange={() => handleToggleDesignatedRow(row.id)}
                                      />
                                    </TableCell>
                                    {idx === 0 ? (
                                      <TableCell 
                                        rowSpan={statusSkus.length} 
                                        sx={{ 
                                          bgcolor: statusConfig?.bgColor || 'grey.100',
                                          fontWeight: 600,
                                          verticalAlign: 'top',
                                          borderRight: `3px solid ${statusConfig?.color || '#000'}`
                                        }}
                                      >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <Box 
                                            sx={{ 
                                              width: 12, 
                                              height: 12, 
                                              borderRadius: '50%', 
                                              bgcolor: statusConfig?.color || '#000'
                                            }} 
                                          />
                                          {statusConfig?.label || status}
                                        </Box>
                                        <Chip 
                                          label={statusSkus.length} 
                                          size="small" 
                                          sx={{ mt: 0.5, fontSize: '0.7rem' }}
                                        />
                                      </TableCell>
                                    ) : null}
                                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                                      {row.tissueId}
                                    </TableCell>
                                    <TableCell>
                                      <Tooltip
                                        title={
                                          row.warehouse
                                            ? `Warehouse: ${row.warehouse} • Zone: ${row.zone || '-'} • Shelf: ${row.shelf || '-'} • Bin: ${row.bin || '-'}`
                                            : 'Location not assigned'
                                        }
                                      >
                                        <span>
                                          {[row.warehouse, row.zone, row.shelf, row.bin].filter(Boolean).join(' / ') || '—'}
                                        </span>
                                      </Tooltip>
                                    </TableCell>
                                  </TableRow>
                                ));
                              })}
                              {Object.values(availableSkusGroups.designatedByStatus).every(arr => arr.length === 0) && (
                                <TableRow>
                                  <TableCell colSpan={4}>
                                    <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                                      <Typography variant="body2">No designated inventory yet.</Typography>
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            ) : (
              // Single table for other tabs
              <TableContainer sx={{ maxHeight: 480, borderRadius: 2, border: '1px solid #e2e8f0' }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          color="primary"
                          indeterminate={selectedSkuIds.length > 0 && selectedSkuIds.length < currentRows.length}
                          checked={currentRows.length > 0 && selectedSkuIds.length === currentRows.length}
                          onChange={handleToggleAll}
                          disabled={isSelectionDisabled || currentRows.length === 0}
                        />
                      </TableCell>
                      <TableCell>Tissue ID</TableCell>
                      <TableCell>SKU</TableCell>
                      <TableCell align="center">Status</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell>Last Updated</TableCell>
                      <TableCell>Location</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {currentRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7}>
                          <Box sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
                            <Typography variant="body2">No tissue IDs in this step.</Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : (
                      currentRows.map((row) => (
                        <TableRow hover key={row.id} selected={rowsSelectedState.has(row.id)}>
                          <TableCell padding="checkbox">
                            <Checkbox
                              color="primary"
                              checked={rowsSelectedState.has(row.id)}
                              onChange={() => handleToggleRow(row.id)}
                              disabled={isSelectionDisabled}
                            />
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                            {row.tissueId}
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace' }}>{row.sku}</TableCell>
                          <TableCell align="center">
                            <Chip
                              size="small"
                              color={
                                row.status === 'AVAILABLE'
                                  ? 'success'
                                  : row.status === 'IN_PROGRESS'
                                    ? 'info'
                                    : row.status === 'ALLOCATED'
                                      ? 'secondary'
                                      : 'warning'
                              }
                              label={
                                row.status === 'AVAILABLE'
                                  ? 'Completed'
                                  : row.status === 'IN_PROGRESS'
                                    ? 'In Progress'
                                    : row.status === 'ALLOCATED'
                                      ? 'Sold / Allocated'
                                      : 'Pending'
                              }
                              sx={{ fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell>{formatDate(row.createdAt)}</TableCell>
                          <TableCell>{formatDate(row.lastUpdatedAt)}</TableCell>
                          <TableCell>
                            <Tooltip
                              title={
                                row.warehouse
                                  ? `Warehouse: ${row.warehouse} • Zone: ${row.zone || '-'} • Shelf: ${row.shelf || '-'} • Bin: ${row.bin || '-'}`
                                  : 'Location not assigned'
                              }
                            >
                              <span>
                                {[row.warehouse, row.zone, row.shelf, row.bin].filter(Boolean).join(' / ') || '—'}
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Stack>
        </CardContent>
      </Card>

      {/* Inventory Designation Dialog */}
      <Dialog open={designationDialogOpen} onClose={() => setDesignationDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#039582', color: 'white' }}>
          Designate Inventory - GDP Compliant
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            All changes are tracked with date, time, and user for Good Documentation Practices compliance.
          </Alert>

          <Stack spacing={3}>
            <FormControl fullWidth>
              <InputLabel>QC Status *</InputLabel>
              <Select
                value={designationForm.qcStatus}
                onChange={(e) => setDesignationForm({ ...designationForm, qcStatus: e.target.value })}
                label="QC Status *"
              >
                {QC_STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box 
                        sx={{ 
                          width: 12, 
                          height: 12, 
                          borderRadius: '50%', 
                          bgcolor: option.color 
                        }} 
                      />
                      {option.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Location (Optional)</InputLabel>
              <Select
                value={designationForm.location}
                onChange={(e) => setDesignationForm({ ...designationForm, location: e.target.value })}
                label="Location (Optional)"
              >
                <MenuItem value="">
                  <em>Keep Current Location</em>
                </MenuItem>
                {designationLocations.map((loc) => (
                  <MenuItem key={loc.id} value={loc.name}>
                    {loc.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Reason for Change *"
              placeholder="Enter the reason for this change..."
              value={designationForm.reason}
              onChange={(e) => setDesignationForm({ ...designationForm, reason: e.target.value })}
              multiline
              rows={3}
              required
              helperText="GDP compliance requires documenting the reason for all inventory changes"
            />

            <Alert severity="warning">
              <Typography variant="body2">
                <strong>{selectedSkuIds.length}</strong> tissue ID{selectedSkuIds.length > 1 ? 's' : ''} will be updated.
              </Typography>
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button 
            onClick={() => setDesignationDialogOpen(false)} 
            disabled={designationLoading}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleBulkDesignation}
            variant="contained"
            disabled={designationLoading || !designationForm.qcStatus || !designationForm.reason.trim()}
            sx={{
              bgcolor: '#039582',
              '&:hover': { bgcolor: '#027A6B' },
              textTransform: 'none'
            }}
          >
            {designationLoading ? 'Updating...' : 'Update with Audit Trail'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Redesignate Dialog for already-designated SKUs */}
      <Dialog open={redesignateDialogOpen} onClose={() => setRedesignateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#8b5cf6', color: 'white' }}>
          {redesignateForm.qcStatus === '' && selectedDesignatedSkuIds.length > 0 
            ? 'Clear Designation / Return to Undesignated' 
            : 'Change Designation - GDP Compliant'}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            {redesignateForm.qcStatus === '' 
              ? 'Selected items will be returned to the undesignated inventory pool.'
              : 'All changes are tracked with date, time, and user for Good Documentation Practices compliance.'}
          </Alert>

          <Stack spacing={3}>
            <FormControl fullWidth>
              <InputLabel>New QC Status</InputLabel>
              <Select
                value={redesignateForm.qcStatus}
                onChange={(e) => setRedesignateForm({ ...redesignateForm, qcStatus: e.target.value })}
                label="New QC Status"
              >
                <MenuItem value="">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ClearIcon fontSize="small" sx={{ color: '#f59e0b' }} />
                    <em>Clear Designation (Return to Undesignated)</em>
                  </Box>
                </MenuItem>
                {QC_STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box 
                        sx={{ 
                          width: 12, 
                          height: 12, 
                          borderRadius: '50%', 
                          bgcolor: option.color 
                        }} 
                      />
                      {option.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Location (Optional)</InputLabel>
              <Select
                value={redesignateForm.location}
                onChange={(e) => setRedesignateForm({ ...redesignateForm, location: e.target.value })}
                label="Location (Optional)"
              >
                <MenuItem value="">
                  <em>Keep Current Location</em>
                </MenuItem>
                {designationLocations.map((loc) => (
                  <MenuItem key={loc.id} value={loc.name}>
                    {loc.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Reason for Change *"
              placeholder="Enter the reason for this change..."
              value={redesignateForm.reason}
              onChange={(e) => setRedesignateForm({ ...redesignateForm, reason: e.target.value })}
              multiline
              rows={3}
              required
              helperText="GDP compliance requires documenting the reason for all inventory changes"
            />

            <Alert severity="warning">
              <Typography variant="body2">
                <strong>{selectedDesignatedSkuIds.length}</strong> tissue ID{selectedDesignatedSkuIds.length > 1 ? 's' : ''} will be updated.
              </Typography>
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button 
            onClick={() => {
              setRedesignateDialogOpen(false);
              setRedesignateForm({ qcStatus: '', location: '', reason: '' });
            }} 
            disabled={designationLoading}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRedesignate}
            variant="contained"
            disabled={designationLoading || !redesignateForm.reason.trim()}
            sx={{
              bgcolor: '#8b5cf6',
              '&:hover': { bgcolor: '#7c3aed' },
              textTransform: 'none'
            }}
          >
            {designationLoading ? 'Updating...' : 'Update with Audit Trail'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReceiveInventoryBatchDetail;

